import type { DocumentContext } from '@/document-render/domain/DocumentContext';
import type { TemplateLogo } from '@/templates/domain/types/TemplateLogo';
import { assertSafeImageUrl } from '@/common/domain/utils/assertSafeImageUrl';
import { readDocumentBranding } from './documentBranding';

/** Same-origin RapidWorks mark used when preview context has no company logo. */
export const DEFAULT_PREVIEW_LOGO_URL = '/quote-pdf-logo.png';

function trimSafeUrl(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  return assertSafeImageUrl(value);
}

export interface ResolveLogoUrlOptions {
  /** When false, ignore context.branding logo fields (template editor surfaces). Default true. */
  allowContextBranding?: boolean;
  /** When false, return null instead of DEFAULT_PREVIEW_LOGO_URL. Default true. */
  allowDefaultFallback?: boolean;
}

/** Resolves a company logo URL from template branding and/or DocumentContext.branding. */
export function resolveLogoUrl(
  logo: TemplateLogo | null | undefined,
  context: DocumentContext,
  options: ResolveLogoUrlOptions = {}
): string | null {
  const { allowContextBranding = true, allowDefaultFallback = true } = options;

  // `assetId` is the *persisted* form of a template logo (`toTemplateLogo` maps `logoAssetId` into
  // it, and `fromTemplateStyling` PUTs it back), so a reloaded template frequently has an empty
  // `url` and only an `assetId`. It is renderable directly whenever it is itself an http(s) or
  // data URL — exactly the treatment `resolvePdfPageBackground` gives `background.imageAssetId`.
  // An opaque share-api blob key cannot be resolved here: the HostConfig contract has no keyed
  // logo resolver (only `resolveBackgroundImageUrl(blobKey)` for backgrounds and a *keyless*
  // `resolveCompanyLogoUrl()`), and this function is shared by the HTML preview and the React-PDF
  // tree, neither of which can await. Such a logo therefore stays unresolved — and in
  // `renderPurpose: 'snapshot'` that fails closed rather than substituting a default (LLAMS-562).
  const fromTemplate = trimSafeUrl(logo?.url) ?? trimSafeUrl(logo?.assetId);
  if (fromTemplate) return fromTemplate;

  if (allowContextBranding) {
    const branding = readDocumentBranding(context);
    const fromContext = trimSafeUrl(branding.logoUrl) ?? trimSafeUrl(branding.logo);
    if (fromContext) return fromContext;
  }

  if (allowDefaultFallback) {
    return trimSafeUrl(DEFAULT_PREVIEW_LOGO_URL);
  }

  return null;
}

/** @deprecated Prefer resolveLogoUrl — kept for cover call sites during Part 5. */
export function resolveCoverLogoUrl(context: DocumentContext): string | null {
  return resolveLogoUrl(undefined, context);
}
